import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, gbp, startCheckout, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const PACKAGES = [
  { key: "sponsor_article", name: "Sponsored Article", unit: "per article", features: ["'Sponsored by' badge on story & cards", "In-article partner card with lead form", "Qualified leads delivered to you", "Permanent placement in archive"] },
  { key: "sponsor_brief_week", name: "Daily Brief Week", unit: "5 daily briefs", features: ["Presenting partner on 5 daily briefs", "Logo in brief headers", "Partner card on each brief"] },
  { key: "sponsor_newsletter", name: "Tuesday Roast Sponsor", unit: "per Tuesday issue", features: ["\"This week's Roast is brought to you by…\" in every subscriber's inbox", "Your message and a link to your website", "Sponsor badge on the Roast on our website", "Pick your Tuesday when you book"] },
  { key: "sponsor_report", name: "Research Partner", unit: "per report", features: ["Co-branded sector report", "Foreword & data contribution", "Lead list of report downloaders", "Launch webinar placement"] },
];

const STATS = [["84%", "senior trade advisors, buyers & investors"], ["UK · EU · Africa", "corridor readership"], ["7", "core sectors"], ["100%", "sponsorship clearly labelled"]];

const InquiryForm = ({ article }) => {
  const [f, setF] = useState({ name: "", email: "", company: "", package: "sponsor_article", budget: "", message: article ? `Interested in sponsoring: ${article}` : "" });
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/sponsorship-inquiries", f); setDone(true); toast.success("Thanks — our partnerships desk will reply within 48 hours."); } catch (err) { toast.error(formatApiError(err)); }
  };
  if (done) return <p data-testid="inquiry-success" className="bg-[#0A0A0A] text-white p-8 font-display text-xl">Thank you. Our partnerships desk will send the media kit and availability within 48 hours.</p>;
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="sponsor-inquiry-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Input data-testid="inquiry-name" required placeholder="Full name" value={f.name} onChange={set("name")} className="rounded-none bg-white h-11" />
        <Input data-testid="inquiry-email" required type="email" placeholder="Work email" value={f.email} onChange={set("email")} className="rounded-none bg-white h-11" />
        <Input data-testid="inquiry-company" required placeholder="Company" value={f.company} onChange={set("company")} className="rounded-none bg-white h-11" />
        <select data-testid="inquiry-package" value={f.package} onChange={set("package")} className="border border-input bg-white h-11 px-3 text-sm">
          {PACKAGES.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          <option value="custom">Custom partnership</option>
        </select>
      </div>
      <Input data-testid="inquiry-budget" placeholder="Indicative budget (optional)" value={f.budget} onChange={set("budget")} className="rounded-none bg-white h-11" />
      <Textarea data-testid="inquiry-message" placeholder="Tell us about your goals" rows={4} value={f.message} onChange={set("message")} className="rounded-none bg-white" />
      <button data-testid="inquiry-submit-btn" className="bg-[#0A0A0A] text-white px-8 py-3 text-sm hover:bg-[#E5332A] transition-colors">Request media kit</button>
    </form>
  );
};

export default function Sponsor() {
  const [params] = useSearchParams();
  const [prices, setPrices] = useState({});
  const [busy, setBusy] = useState("");
  const [articleTitle, setArticleTitle] = useState("");
  const [tuesdays, setTuesdays] = useState([]);
  const [roastDate, setRoastDate] = useState("");
  useEffect(() => { api.get("/plans").then((r) => setPrices(r.data.prices)); }, []);
  useEffect(() => {
    api.get("/roast/availability").then((r) => { setTuesdays(r.data); setRoastDate(r.data.find((t) => !t.booked)?.date || ""); });
  }, []);
  useEffect(() => {
    const s = params.get("article");
    if (s) api.get(`/articles/${s}`).then((r) => setArticleTitle(r.data.title)).catch(() => setArticleTitle(s));
  }, [params]);
  const book = async (key) => {
    setBusy(key);
    if (key === "sponsor_newsletter" && !roastDate) { toast.error("Pick a Tuesday for your sponsorship"); setBusy(""); return; }
    try { await startCheckout(key, { article_slug: params.get("article") || undefined, roast_date: key === "sponsor_newsletter" ? roastDate : undefined }); } catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };

  return (
    <div data-testid="sponsor-page">
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <p className="overline text-[#E5332A]">Partner with Africa Brew Brief</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 leading-[1.05]">Reach the people building bridges between Africa and the world.</h1>
          <p className="text-base md:text-lg text-[#575757] mt-6 max-w-2xl leading-relaxed">Sponsor individual articles, daily briefs, newsletters or full research reports. Every placement is clearly labelled, contextually relevant and lead-generating.</p>
          {params.get("article") && <p data-testid="sponsor-article-context" className="mt-6 inline-block bg-[#F6F6F6] border border-[#DADADA] text-[#E5332A] text-sm px-4 py-2">You're enquiring about sponsoring: <b>{articleTitle || params.get("article")}</b></p>}
        </div>
        <div className="lg:col-span-4 lg:col-start-9 grid grid-cols-2 gap-px bg-[#E7E7E7] border border-[#E7E7E7] self-end">
          {STATS.map(([n, l]) => <div key={l} className="bg-[#FFFFFF] p-5"><p className="font-display text-2xl font-bold text-[#0A0A0A]">{n}</p><p className="text-[11px] text-[#575757] mt-1 uppercase tracking-wider">{l}</p></div>)}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PACKAGES.map((p) => (
          <div key={p.key} data-testid={`sponsor-package-${p.key}`} className="border border-[#E7E7E7] bg-white p-6 flex flex-col">
            <p className="overline text-[#E5332A]">{p.unit}</p>
            <p className="font-display text-2xl font-bold mt-2">{p.name}</p>
            <p className="font-mono-d text-lg mt-4">{prices[p.key] ? gbp(prices[p.key].amount) : "—"}</p>
            <ul className="mt-5 space-y-2 text-sm flex-1">{p.features.map((f) => <li key={f} className="flex gap-2"><Check size={15} className="text-[#E5332A] shrink-0 mt-0.5" /> {f}</li>)}</ul>
            {p.key === "sponsor_newsletter" && (
              <label className="mt-5 block text-xs text-[#575757]">Your Tuesday
                <select data-testid="roast-sponsor-date-select" value={roastDate} onChange={(e) => setRoastDate(e.target.value)} className="mt-1 w-full border border-input bg-white h-10 px-2 text-sm text-[#0A0A0A]">
                  {tuesdays.map((t) => <option key={t.date} value={t.date} disabled={t.booked}>{new Date(`${t.date}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}{t.booked ? " · booked" : ""}</option>)}
                </select>
              </label>
            )}
            <button data-testid={`sponsor-book-${p.key}`} onClick={() => book(p.key)} disabled={!!busy} className="mt-6 bg-[#0A0A0A] text-white py-3 text-sm hover:bg-[#E5332A] transition-colors">{busy === p.key ? "Opening checkout…" : "Book & pay now"}</button>
          </div>
        ))}
      </section>
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <h2 className="font-display text-3xl font-bold">Prefer to talk first?</h2>
          <p className="text-[#575757] mt-4 leading-relaxed">Request the media kit, editorial calendar and custom partnership options. Editorial independence is non-negotiable; sponsors never influence our reporting.</p>
        </div>
        <div className="lg:col-span-7 lg:col-start-6"><InquiryForm article={params.get("article")} /></div>
      </section>
    </div>
  );
}
